/**
 * Owner: Masa Bayu
 * File: qxp-artifact-registry.ts
 * Date:Time: 2026-10-17 20:15
 * Description: Fasa 1 BP3 + §422 K1 — muat artifak QXP sebenar (package.qxp + payloads)
 *   ke registry sync. HANYA melalui sumber qxp-hub (register semasa sync katalog);
 *   laluan statik public/ DIBUANG. Read Sample hanya sampleContentRefs.
 *
 * Reader chain (createReaderModel → projectQxpReaderPages → openQxpReaderStack) adalah
 * synchronous — artifak HTTP dimuat lebih awal (semak NaturalReaderScreen) dan disimpan
 * di sini. Wiring membaca registry; jika belum dimuat → denied, tiada fabrikasi.
 */

import type { QXPPackage } from '@qx/qxp-core';
import { assertProductionQxpPackageIntegrity } from '../../host-catalog/qxp-package-integrity';
import { hubAuthHeaders } from '../../publication/hub-auth';
import type { QxpPagePayload } from './qxp-demo-package';
import type { ReaderPageGeometry, ReaderTextLine } from './reader-types';
import {
  layoutOfPayload,
  pageNumberOfPayload,
  segmentsOfPayload,
  type QxpSheetLayout,
  type QxpSheetSegment,
} from './qxp-sheet-payload';
import { payloadTargetsForLoad, underlayPathsForLoad } from './qxp-artifact-sample-scope';
import { clearFigureCache, figurePathsByPageFromManifest } from './qxp-artifact-figures';

export interface QxpArtifactPayload extends QxpPagePayload {
  readonly pageNumber: number | null;
  readonly lineNumber: number | null;
  /** §446 F-2 — geometri sumber baris; undefined = payload tidak membawa layout (laluan lama). */
  readonly line?: ReaderTextLine;
  readonly sheetElementId: string;
  readonly sheetLayout: QxpSheetLayout | null;
  readonly sheetSegments: readonly QxpSheetSegment[] | null;
}

export interface QxpLoadedArtifact {
  readonly package: QXPPackage;
  readonly payloads: ReadonlyMap<string, QxpArtifactPayload>;
  readonly title: string;
  readonly languageRef: string;
  /** §423e B4 — pageNumber → dataURL gambar latar (Pilihan B); halaman tiada = tiada underlay. */
  readonly underlays: ReadonlyMap<number, string>;
  /** Paths only — binaries load per visible page via loadQxpPageFigures. */
  readonly figurePathsByPage: ReadonlyMap<number, readonly string[]>;
  /** §423f PG-3 — geometri halaman buku (px raster) dari manifest; null = kontrak lama. */
  readonly pageGeometry: ReaderPageGeometry | null;
}

// §422 K1 — TIADA laluan statik. Sumber artifak hanya didaftarkan oleh syncQxpHub
// selepas descriptor disahkan bentuknya (ruling Owner: bacaan sahaja melalui QXReader
// dengan gerbang DRM hub). Pintu integriti penuh kekal: manifest mesti sepadan
// publicationId + package.qxp lulus assertProductionQxpPackageIntegrity.
const hubArtifactSources = new Map<string, string>();

type LoadedEntry = {
  readonly artifact: QxpLoadedArtifact;
  readonly sampleOnly: boolean;
};

const loadedArtifacts = new Map<string, LoadedEntry>();
const inflightLoads = new Map<string, Promise<boolean>>();

type ArtifactManifest = {
  readonly kind?: unknown;
  readonly publicationId?: unknown;
  readonly title?: unknown;
  readonly languageRef?: unknown;
  readonly pageUnderlays?: unknown;
  readonly pageFigures?: unknown;
  readonly pageGeometry?: unknown;
  readonly sampleContentRefs?: unknown;
  readonly sampleMaxPage?: unknown;
};

type ArtifactPayloadFile = {
  readonly targetRef?: unknown;
  readonly elementId?: unknown;
  readonly text?: unknown;
  readonly layout?: unknown;
  readonly font?: unknown;
  readonly page?: unknown;
  readonly linePaint?: unknown;
};

type ArtifactUnderlayFile = {
  readonly kind?: unknown;
  readonly page?: unknown;
  readonly data?: unknown;
};

export interface QxpArtifactResponse {
  readonly ok: boolean;
  readonly status?: number;
  readonly json: () => Promise<unknown>;
  readonly text?: () => Promise<string>;
}

export type QxpFetchInit = {
  readonly method?: string;
  readonly headers?: Readonly<Record<string, string>>;
};

export type FetchLike = (url: string, init?: QxpFetchInit) => Promise<QxpArtifactResponse>;

async function defaultFetch(url: string, init?: QxpFetchInit): Promise<QxpArtifactResponse> {
  const res = await fetch(url, init);
  const raw = await res.text();
  return {
    ok: res.ok,
    status: res.status,
    json: async () => JSON.parse(raw) as unknown,
    text: async () => raw,
  };
}

/** Header auth gabungan — muatan terpaggar (DRM) memerlukan Bearer bergrant. */
function artifactFetchHeaders(): Record<string, string> {
  return { ...hubAuthHeaders() };
}

export function isQxpArtifactPublicationId(publicationId: string): boolean {
  return hubArtifactSources.has(publicationId.trim());
}

/** Daftarkan sumber artifak hub (base URL mutlak) untuk satu publication id. */
export function registerQxpArtifactSource(publicationId: string, baseUrl: string): void {
  const id = publicationId.trim();
  const base = baseUrl.trim().replace(/\/+$/, '');
  if (id.length === 0 || base.length === 0) return;
  hubArtifactSources.set(id, base);
}

/** Ujian sahaja. */
export function __clearQxpArtifactSourcesForTests(): void {
  hubArtifactSources.clear();
}

export function getLoadedQxpArtifact(publicationId: string): QxpLoadedArtifact | null {
  return loadedArtifacts.get(publicationId.trim())?.artifact ?? null;
}

export function getRegisteredQxpArtifactBase(publicationId: string): string | null {
  return hubArtifactSources.get(publicationId.trim()) ?? null;
}

/** true jika cache memenuhi permintaan (sampel boleh guna cache penuh). */
export function loadedQxpArtifactSatisfies(
  publicationId: string,
  sample: boolean,
): boolean {
  const entry = loadedArtifacts.get(publicationId.trim());
  if (entry === undefined) {
    return false;
  }
  return sample || !entry.sampleOnly;
}

/**
 * Buang artifak yang sudah dimuat supaya muatan seterusnya dimuat semula — wajib
 * selepas grant baharu (muatan sampel terpaggar kekal separa dalam cache; tanpa
 * ini pembeli melihat sampel lama walaupun server telah menggrants akses penuh).
 */
export function invalidateLoadedHubArtifacts(publicationId: string): void {
  const id = publicationId.trim();
  loadedArtifacts.delete(id);
  inflightLoads.delete(id);
}

/**
 * §509 F4 — buang SEMUA muatan artifak daripada ingatan. Wajib apabila sesi berakhir
 * (log keluar / 401): `loadedArtifacts` berkunci publicationId sahaja, tanpa
 * pemilikan sesi, jadi halaman penuh yang dimuat oleh pemilik kekal boleh dibaca
 * oleh pelawat berikutnya dalam tab yang sama jika tidak dibuang.
 */
export function invalidateAllLoadedHubArtifacts(): void {
  loadedArtifacts.clear();
  inflightLoads.clear();
}

function parseLineRef(targetRef: string): {
  readonly pageNumber: number | null;
  readonly lineNumber: number | null;
} {
  // B2a — unit terbitan boleh perbaris (el_pN_line_M) atau perenggan (el_pN_para_M);
  // kedua-duanya berkumpulan halaman yang sama, index kedua ialah nombor baris/perenggan.
  const match = /el_p(\d+)_(?:line|para)_(\d+)/.exec(targetRef);
  if (match === null) return { pageNumber: null, lineNumber: null };
  return {
    pageNumber: Number.parseInt(match[1] ?? '', 10),
    lineNumber: Number.parseInt(match[2] ?? '', 10),
  };
}

/**
 * §423f PG-3 — geometri manifest (px raster) dibaca longgar: pasangan integer
 * positif sahaja diterima; selain itu null → reader kekalkan kotak lama (serasi).
 */
function parsePageGeometry(raw: unknown): ReaderPageGeometry | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const width = (raw as { width?: unknown }).width;
  const height = (raw as { height?: unknown }).height;
  if (
    typeof width !== 'number' ||
    typeof height !== 'number' ||
    !Number.isInteger(width) ||
    !Number.isInteger(height) ||
    width < 1 ||
    height < 1
  ) {
    return null;
  }
  return Object.freeze({ width, height });
}

/**
 * §446 F-2 — geometri baris sumber dibaca longgar: hanya x/y/width/height +
 * font.size finite dan size positif diterima. Selain itu undefined → halaman
 * itu kekal laluan aliran lama (buku tanpa layout, atau payload rosak medan).
 * `layout.y` ialah TOP kotak (bukti drop-cap §446 §1), unit = halaman sumber.
 */
function parseSourceLine(payload: ArtifactPayloadFile): ReaderTextLine | undefined {
  const layout = payload.layout;
  const font = payload.font;
  if (typeof layout !== 'object' || layout === null || typeof font !== 'object' || font === null) {
    return undefined;
  }
  const box = layout as Record<string, unknown>;
  const face = font as Record<string, unknown>;
  const size = face.size;
  if (
    typeof box.x !== 'number' ||
    typeof box.y !== 'number' ||
    typeof box.width !== 'number' ||
    typeof box.height !== 'number' ||
    typeof size !== 'number' ||
    !Number.isFinite(box.x) ||
    !Number.isFinite(box.y) ||
    !Number.isFinite(box.width) ||
    !Number.isFinite(box.height) ||
    !Number.isFinite(size) ||
    size <= 0
  ) {
    return undefined;
  }
  return Object.freeze({
    text: typeof payload.text === 'string' ? payload.text : '',
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
    fontSize: size,
    rotation: typeof box.rotation === 'number' && Number.isFinite(box.rotation) ? box.rotation : 0,
    zIndex: typeof box.zIndex === 'number' && Number.isFinite(box.zIndex) ? box.zIndex : 0,
  });
}

/**
 * §423e B4 — muat underlay halaman dari manifest (laluan content/page-N.json).
 * Sifatnya dekoratif: 401/403 (belum bergrant) dan sebarang kecacatan fail →
 * halaman itu dilewati, bukan seluruh muatan deny.
 */
async function readOneUnderlay(
  base: string,
  rawPath: string,
  headers: Record<string, string>,
  fetchImpl: FetchLike,
): Promise<{ readonly page: number; readonly data: string } | null> {
  if (!rawPath.startsWith('content/')) {
    return null;
  }
  try {
    const res = await fetchImpl(`${base}/${rawPath}`, { headers });
    if (!res.ok) {
      return null;
    }
    const file = (await res.json()) as ArtifactUnderlayFile;
    if (file.kind !== 'qxp_page_underlay') {
      return null;
    }
    if (typeof file.page !== 'number' || !Number.isInteger(file.page) || file.page < 1) {
      return null;
    }
    if (typeof file.data !== 'string' || !file.data.startsWith('data:image/')) {
      return null;
    }
    return { page: file.page, data: file.data };
  } catch {
    return null;
  }
}

async function loadArtifactUnderlays(
  base: string,
  rawPaths: unknown,
  headers: Record<string, string>,
  fetchImpl: FetchLike,
  parallel: boolean,
): Promise<Map<number, string>> {
  const underlays = new Map<number, string>();
  if (!Array.isArray(rawPaths)) {
    return underlays;
  }
  const paths = rawPaths.filter((path): path is string => typeof path === 'string');
  if (parallel) {
    const found = await Promise.all(
      paths.map((path) => readOneUnderlay(base, path, headers, fetchImpl)),
    );
    for (const item of found) {
      if (item !== null) {
        underlays.set(item.page, item.data);
      }
    }
    return underlays;
  }
  for (const rawPath of paths) {
    const item = await readOneUnderlay(base, rawPath, headers, fetchImpl);
    if (item !== null) {
      underlays.set(item.page, item.data);
    }
  }
  return underlays;
}

export type LoadQxpArtifactOptions = {
  readonly sample?: boolean;
};

/**
 * Muat manifest + package.qxp + payload ke registry. Idempotent.
 * Read Sample (`sample: true`) hanya `sampleContentRefs` — bukan seluruh naskhah.
 * Fail-closed: kegagalan (bukan 401/403) → false, tidak dicache.
 */
export async function loadQxpArtifact(
  publicationId: string,
  fetchImpl: FetchLike = defaultFetch,
  options: LoadQxpArtifactOptions = {},
): Promise<boolean> {
  const id = publicationId.trim();
  const sample = options.sample === true;
  if (!isQxpArtifactPublicationId(id)) return false;
  if (loadedQxpArtifactSatisfies(id, sample)) return true;
  const inflightKey = `${id}:${sample ? 's' : 'f'}`;
  const inflight = inflightLoads.get(inflightKey);
  if (inflight !== undefined) return inflight;

  const task = (async (): Promise<boolean> => {
    try {
      const headers = artifactFetchHeaders();
      // Sumber hub = base MUTLAK (origin + artifactBaseUrl termasuk direktori versi
      // /vN daripada descriptor) — satu-satunya laluan; gerbang DRM di pelayan.
      const base = hubArtifactSources.get(id);
      if (base === undefined) return false;
      const manifestRes = await fetchImpl(`${base}/manifest.json`, { headers });
      if (!manifestRes.ok) return false;
      const manifest = (await manifestRes.json()) as ArtifactManifest;
      if (manifest.kind !== 'qxp_v1_artifact' || manifest.publicationId !== id) return false;

      const packageRes = await fetchImpl(`${base}/package.qxp`, { headers });
      if (!packageRes.ok) return false;
      const gate = assertProductionQxpPackageIntegrity({
        body: await packageRes.json(),
        expectedPublicationId: id,
      });
      if (!gate.ok) return false;

      const pkg = gate.package;
      const packageTargets = pkg.contentRefs
        .filter(
          (ref) =>
            ref.kind === 'text' && typeof ref.targetRef === 'string' && ref.targetRef.length > 0,
        )
        .map((ref) => ref.targetRef);
      const targets = payloadTargetsForLoad(
        packageTargets,
        sample,
        manifest.sampleContentRefs,
        manifest.sampleMaxPage,
      );

      const payloads = new Map<string, QxpArtifactPayload>();
      const CHUNK = 40;
      for (let i = 0; i < targets.length; i += CHUNK) {
        const chunk = targets.slice(i, i + CHUNK);
        const results = await Promise.all(
          chunk.map(async (targetRef) => {
            const rel = targetRef.replace(/^[a-z][a-z0-9+-]*:\/\//i, '');
            const res = await fetchImpl(`${base}/content/${rel}.json`, { headers });
            if (!res.ok) {
              // DRM lembut: 401/403 = muatan terpaggar (perlu grant) — langkau, bukan gagal.
              // Muatan sampel kekal dimuat; selepas belian cache dibuang dan dimuat semula penuh.
              const status = res.status ?? (res.ok ? 200 : 500);
              if (status === 401 || status === 403) return { targetRef, item: null, gated: true };
              return { targetRef, item: null, gated: false };
            }
            const payload = (await res.json()) as ArtifactPayloadFile;
            if (payload.targetRef !== targetRef || typeof payload.text !== 'string') {
              return { targetRef, item: null, gated: false };
            }
            return {
              targetRef,
              item: {
                targetRef,
                text: payload.text,
                elementId: typeof payload.elementId === 'string' ? payload.elementId : null,
                // §446 F-2 — geometri sumber ikut laluan payload, bukan dibuang.
                line: parseSourceLine(payload),
                sheetLayout: layoutOfPayload(payload),
                sheetSegments: segmentsOfPayload(payload),
                pageFromFile: pageNumberOfPayload(payload),
              },
              gated: false,
            };
          }),
        );
        for (const result of results) {
          if (result.gated) continue;
          if (result.item === null) return false;
          const item = result.item;
          const parsedRef = parseLineRef(item.targetRef);
          const pageNumber = item.pageFromFile ?? parsedRef.pageNumber;
          const lineNumber = parsedRef.lineNumber;
          const sheetElementId = item.elementId ?? item.targetRef;
          if (pageNumber !== null) {
            payloads.set(item.targetRef, {
              chapterId: `page-${String(pageNumber)}`,
              chapterTitle: `Page ${String(pageNumber)}`,
              body: item.text,
              pageNumber,
              lineNumber,
              sheetElementId,
              sheetLayout: item.sheetLayout,
              sheetSegments: item.sheetSegments,
              ...(item.line !== undefined ? { line: item.line } : {}),
            });
            continue;
          }
          // Emit qxwriter — satu elemen = satu bab; baris pertama ialah tajuk bab.
          const newlineIndex = item.text.indexOf('\n');
          const firstLine = (
            newlineIndex === -1 ? item.text : item.text.slice(0, newlineIndex)
          ).trim();
          const rest = newlineIndex === -1 ? '' : item.text.slice(newlineIndex + 1).trim();
          payloads.set(item.targetRef, {
            chapterId: item.elementId ?? 'artifact',
            chapterTitle: firstLine.length > 0 ? firstLine : 'Artifact',
            body: rest.length > 0 ? rest : item.text,
            pageNumber: null,
            lineNumber: null,
            sheetElementId,
            sheetLayout: item.sheetLayout,
            sheetSegments: item.sheetSegments,
          });
        }
      }

      const underlays = await loadArtifactUnderlays(
        base,
        underlayPathsForLoad(manifest.pageUnderlays, sample, manifest.sampleContentRefs),
        headers,
        fetchImpl,
        sample,
      );

      loadedArtifacts.set(
        id,
        Object.freeze({
          artifact: Object.freeze({
            package: pkg,
            payloads,
            title: typeof manifest.title === 'string' ? manifest.title : id,
            languageRef: typeof manifest.languageRef === 'string' ? manifest.languageRef : 'en',
            underlays,
            figurePathsByPage: figurePathsByPageFromManifest(manifest.pageFigures),
            pageGeometry: parsePageGeometry(manifest.pageGeometry),
          }),
          sampleOnly: sample,
        }),
      );
      return true;
    } catch {
      return false;
    } finally {
      inflightLoads.delete(inflightKey);
    }
  })();

  inflightLoads.set(inflightKey, task);
  return task;
}

/** Ujian sahaja — buang cache supaya setiap kes berdiri sendiri. */
export function __clearQxpArtifactRegistryForTests(): void {
  loadedArtifacts.clear();
  inflightLoads.clear();
  clearFigureCache();
}
